import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuSeparator, ContextMenuTrigger } from "@/ui/context-menu";
import { ICONES, Icone, TITULOS, dataHora, quandoCurto, tinta } from "../visual.jsx";
import { useAcoes, useCelular, useEstado, useTablet } from "../estado.jsx";
import { useNavegacao } from "../navegacao.jsx";
import { chaveDaEtiqueta, coresDoCatalogo } from "../etiquetas.js";
import { comoFrase, comoNome, semAcento } from "../nomes.js";
import { conectado, numeroCurto, numeroLegivel } from "../whatsapp-estado.js";
import { Botao, CampoDeBusca, Contador, Etiqueta, Retrato, Rosto, Vazio } from "./comuns.jsx";
import ListaLonga, { RodapeDaPagina } from "./ListaLonga.jsx";
import FiltroDaMesa from "./FiltroDaMesa.jsx";
import SelecaoDeCasos from "./SelecaoDeCasos.jsx";

/** O que a linha da lista mostra quando a última mensagem é só anexo. */
const ROTULO_DO_TIPO = {
  image: "foto",
  audio: "áudio",
  video: "vídeo",
  document: "documento",
  sticker: "figurinha",
  note: "nota interna",
};

/* Fora do componente: objeto de estilo novo a cada render entra como prop nova
   em duzentas linhas memoizadas. É o véu de 8%, o mesmo do `:active` de `.lin` —
   aberta e marcada falam o mesmo tom que o toque. */
const FUNDO_ATIVO = { background: "color-mix(in oklab, var(--color-foreground) 8%, transparent)" };

/*
 * A partir de quantos minutos com a bola parada aqui a linha diz que o cliente
 * está esperando.
 *
 * É a MESMA tolerância do `SEM_RESPOSTA` de `src/leads.ts`, que já decide a
 * pastilha "Prazo hoje" — o servidor mandava `minutos_sem_resposta` em toda
 * linha e a lista nunca mostrava: a espera existia na consulta, na pastilha e
 * em lugar nenhum na tela. Duplicar o 15 aqui é barato; o que não pode é a
 * pastilha contar um caso que a linha não confessa.
 */
const MINUTOS_DE_TOLERANCIA = 15;

/* A cor da espera sai do token da casa, não de um hexadecimal novo: o véu é o
   mesmo `--color-veu-espera` que qualquer outro estado terracota da mesa. */
const CORES_DA_ESPERA = ["var(--color-veu-espera)", "var(--color-terra)"];
/* O arquivado é a etiqueta `discreta` (véu de 8% e tinta-suave): cinza e não
   colorido de propósito — ele diz "isto está fora", e cor forte competiria com a
   espera e com a etiqueta do escritório, que são o trabalho de agora. */

/**
 * O foco está num lugar onde se ESCREVE?
 *
 * Atalho de tecla solta ("/" para buscar, Alt+seta para andar na lista) não
 * pode existir dentro de campo de texto: a mesma tecla ali é conteúdo, ou é
 * outro gesto — o "/" do composer abre as respostas prontas.
 */
const escrevendo = () => {
  const onde = document.activeElement;
  return Boolean(onde && (onde.isContentEditable || /^(input|textarea|select)$/i.test(onde.tagName)));
};

/** Quanto tempo o cliente está esperando, em palavra curta: "35min", "2h", "3d". */
const espera = (min) => (min < 60 ? `${min}min` : min < 1440 ? `${Math.round(min / 60)}h` : `${Math.round(min / 1440)}d`);

/**
 * De quem é o atendimento, no canto do retrato do cliente.
 *
 * O nome do titular vinha em toda linha da mesa (`responsavel`) e morria no
 * caminho: para saber de quem era um caso era preciso abrir a ficha, um a um. O
 * rosto resolve numa olhada, e ocupa 20px — o nome por extenso não caberia sem
 * roubar a tese.
 *
 * Sem titular o lugar NÃO fica vazio: um círculo tracejado diz "está na fila",
 * que é uma informação, não a ausência de uma. Espaço em branco na mesma
 * posição se lê como "ainda não carregou".
 */
/**
 * O rosto de quem responde pelo caso AGORA — e quando a IA está no comando, é
 * ela.
 *
 * A linha mostrava o retrato do titular humano e um pontinho verde no retrato do
 * CLIENTE (com o texto só na dica): quem varria a mesa lia "caso da Camila" num
 * atendimento em que a Camila não ia responder nada — a IA já estava
 * respondendo. Agora o rosto é o do agente, em verde, com o nome dele e o do
 * humano que recebe de volta escritos na dica.
 */
function Titular({ nome, cor, foto, ia, iaDeLa }) {
  /* A IA do LIDERHUB (02/11): o escritório ainda atende por lá, e o cliente pode
     estar conversando com o robô de lá. Mesmo símbolo, cor de tinta e não o
     verde — o verde é a IA DAQUI, e confundir as duas é achar que o CRM está
     respondendo um cliente que ele nem vê. */
  if (!ia && iaDeLa)
    return (
      <span
        data-dica={`No LiderHub, a IA está atendendo este cliente (${iaDeLa})`}
        aria-label={`IA do LiderHub: ${iaDeLa}`}
        className="flex size-5 flex-none items-center justify-center rounded-full bg-tinta-suave text-creme"
      >
        <Icone de={ICONES.agenteIa} tamanho={14} />
      </span>
    );
  if (ia)
    return (
      <span
        data-dica={`${ia.nome} está conduzindo este atendimento${nome ? ` — volta para ${nome} quando a IA devolver` : ""}`}
        aria-label={`No comando: ${ia.nome}`}
        /* O ÍCONE da casa (o mesmo que marca a IA no trilho), e não o emoji do
           agente: emoji é escolha do escritório para dar cara à persona — ele
           muda de agente para agente e é desenhado pelo sistema operacional, em
           cinco cores, num círculo de 20px. Aqui o que se lê de relance é "quem
           responde por este caso não é gente", e isso é sempre o mesmo símbolo.
           O nome (e a persona) continuam na dica. */
        className="flex size-5 flex-none items-center justify-center rounded-full bg-verde text-sobre-terra ring-2 ring-verde-vivo/40"
      >
        <Icone de={ICONES.agenteIa} tamanho={14} />
      </span>
    );
  if (!nome)
    return (
      <span
        data-dica="Sem titular — está na fila"
        className="size-5 flex-none rounded-full border border-dashed border-terra/50"
      />
    );
  return (
    <span data-dica={`Titular: ${nome}`} className="flex-none">
      {/* A FOTO que a pessoa subiu em Equipe (`people.avatar`, servida por
          `/api/avatar/:arquivo`), e as iniciais na cor do cadastro (`people.cor`)
          quando não há foto. É o mesmo par que a transferência e o cartão da
          equipe já desenham — desenhar só as iniciais aqui fazia a mesma pessoa
          ter dois rostos na mesma tela. */}
      <Rosto pessoa={nome} foto={foto} cor={cor} tamanho={20} />
    </span>
  );
}

/**
 * Uma conversa na lista. Memoizada: são 200 linhas e o estado muda o tempo todo.
 *
 * Continua um `<button>` da casa, e não o `Item` do shadcn: `Item` é uma linha
 * arredondada de conteúdo centrado, e `ItemGroup` declara `role=list` — dentro
 * do `ListaLonga`, que já marca a lista e cada `role=listitem`, seria uma
 * segunda lista por linha. O que o `Item` traria de graça (o realce ao passar) já
 * é da classe `.lin`, que a mesa inteira usa.
 */
const LinhaDoCaso = memo(function LinhaDoCaso({
  caso,
  ativo,
  corDoTitular,
  fotoDoTitular,
  aoAbrir,
  aoArquivar,
  aoDesarquivar,
  aoApagar,
  aoEtiquetar,
  aoNaoLer,
  coresDasTags,
  selecionando,
  marcado,
  aoMarcar,
  numeroDaLinha,
  numeroDesligado,
  numeroDica,
}) {
  /* Bolha que é só anexo não tem texto: sem o rótulo do tipo a linha dizia "sem
     mensagens ainda" numa conversa que acabou de receber uma foto. */
  const resumo = caso.ultima?.trim() || (caso.msg_id ? (ROTULO_DO_TIPO[caso.ultima_tipo] ?? "mensagem") : "");
  const ultima = resumo ? (caso.ultima_direcao === "saida" ? `Você: ${resumo}` : resumo) : "sem mensagens ainda";
  /* A bola está com o escritório e já passou da tolerância: é isto que a
     pastilha "Prazo hoje" conta, e o que a coluna precisa dizer sem filtro
     nenhum ligado — a fila do dia se lê correndo o olho, não abrindo pastilha. */
  const esperando = caso.minutos_sem_resposta != null && caso.minutos_sem_resposta >= MINUTOS_DE_TOLERANCIA;
  const tags = (caso.tags ? caso.tags.split("|") : []).filter((t) => !caso.tese || semAcento(t) !== semAcento(caso.tese));
  return (
    <ContextMenu>
      <ContextMenuTrigger
        /* O gatilho É a linha: `render` reaproveita o mesmo `<button>` em vez de
           embrulhá-lo num `<div>`, que mudaria o layout da coluna e poria um
           elemento a mais entre a lista e o item. Botão direito no computador,
           toque longo no celular — os dois vêm da primitiva. */
        render={
          <button
            /* Em modo seleção o clique MARCA, e não abre: o gesto de uma lista
               com caixas é esse, e abrir a conversa no meio de escolher trinta
               casos joga fora a escolha inteira. */
            onClick={() => (selecionando ? aoMarcar(caso.id) : aoAbrir(caso.id))}
            aria-pressed={selecionando ? marcado : undefined}
            /* A conversa aberta se dizia só pela borda esquerda: `aria-current` é o que
         diz "é esta" a quem não vê a borda. */
            aria-current={ativo ? "true" : undefined}
            /* Linha rente e divisória fina embaixo, como a lista do WhatsApp Web: o
         cartão arredondado com borda laranja à esquerda transformava a coluna
         numa pilha de fichas, e a divisória é o que faz duas conversas
         seguidas se lerem como uma lista. A aberta se diz pelo fundo. */
            /* O toque escurece o fundo (`.lin:active`, véu de 8%) e a linha NÃO
               encolhe: `active:transform-none` desfaz o .97 que todo `<button>`
               ganha na base — 3% de uma linha de 380px é a coluna tremendo.

               O foco é anel POR DENTRO: a lista rola com `overflow-x:hidden`
               (`ListaLonga`), e o anel afastado 2px da casa era cortado nas
               duas laterais. `outline-none!` porque a regra do anel é de fora
               de camada e só perde para `!important`.

               A aberta leva o fio terra de 3px à esquerda (`before:`), além do
               fundo: fundo de 8% sozinho se confundia com o hover. */
            className={`lin relative flex w-full cursor-pointer touch-manipulation items-start gap-3 border-b border-b-foreground/8 bg-transparent px-4 py-3 text-left active:transform-none focus-visible:outline-none! focus-visible:shadow-[inset_0_0_0_2px_var(--cor-anel)] ${
              ativo
                ? "before:absolute before:inset-y-2.5 before:left-0 before:w-[3px] before:rounded-r-[3px] before:bg-terra"
                : ""
            }`}
            /* O fundo da conversa aberta fica no `style`, e não em classe: `.lin:hover`
         (index.html) tem duas classes de especificidade e clarearia a linha
         aberta ao passar o mouse — o realce diria menos que o repouso. A
         transição e o hover são de `.lin`; não se repetem aqui. */
            style={ativo || (selecionando && marcado) ? FUNDO_ATIVO : undefined}
          >
            {selecionando && (
              <span
                /* NÃO é o `Checkbox` do registro, e o motivo é estrutural: a
                   linha inteira já é o `<button>` (é o alvo, e no dedo tem de
                   ser), e o Base UI desenha a caixa como outro `<button>` —
                   botão dentro de botão é marcação inválida e o clique deixa de
                   chegar na linha. Aqui a caixa é o DESENHO do estado; quem
                   responde ao toque, ao teclado e ao leitor de tela é a linha,
                   pelo `aria-pressed`. */
                /* Marcada em TINTA, como o ✓ do cabeçalho que liga a seleção:
                   era verde aqui e tinta lá — duas cores para o mesmo estado. */
                aria-hidden="true"
                className={`mt-[13px] flex size-[22px] flex-none items-center justify-center rounded-full border-2 transition-colors duration-[var(--t-toque)] ${
                  marcado ? "border-tinta bg-tinta text-mesa" : "border-borda-controle"
                }`}
              >
                {marcado && <Icone de={ICONES.certo} tamanho={14} />}
              </span>
            )}
            <div className="relative flex-none">
              {/* A foto do WhatsApp quando o aparelho a trouxe; iniciais quando não. */}
              {/* Arquivado apaga o ROSTO, não o texto: o nome continua sendo
                  lido, e o cinza no retrato diz "fora da mesa" antes da etiqueta. */}
              <Retrato nome={caso.nome} registro={caso.id} tamanho={48} apagado={Boolean(caso.arquivado)} />
              {/* De quem é o caso, no canto do retrato, onde ficava a bandeira do
                  DDD (17/10): o estado do cliente se lê no cabeçalho da conversa,
                  e "de quem é" é a pergunta que se faz varrendo a lista. O anel
                  na cor do fundo descola o rosto pequeno do grande. */}
              <span className="absolute -right-1 -bottom-1 flex rounded-full bg-background p-0.5">
                <Titular
                  nome={caso.responsavel}
                  cor={corDoTitular}
                  foto={fotoDoTitular}
                  ia={caso.delegado ? { nome: caso.delegado_agente || "A IA do escritório", emoji: caso.delegado_emoji } : null}
                  iaDeLa={caso.liderhub_ia}
                />
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline justify-between gap-2">
                {/* 15/600, e a não lida muda o PESO (700), não a cor: quem varre
                    a coluna acha o nome em negrito sem precisar do contador. */}
                <span
                  className={`overflow-hidden text-[15px] leading-[1.3] text-ellipsis whitespace-nowrap ${
                    caso.nao_lidas ? "font-bold" : caso.arquivado ? "font-medium text-tinta-suave" : "font-semibold"
                  }`}
                >
                  {comoNome(caso.nome)}
                </span>
                {/* O carimbo é curto na linha e completo na dica: cabe na coluna
                    estreita e ainda responde "que dia foi isso?" sem abrir o caso. */}
                <span
                  data-dica={caso.ultima_em ? dataHora(caso.ultima_em) : undefined}
                  className={`flex-none font-mono text-[12px] leading-none tabular-nums ${
                    caso.nao_lidas ? "font-bold text-terra" : "font-medium text-tinta-fraca"
                  }`}
                >
                  {quandoCurto(caso.ultima_em)}
                </span>
              </div>
              <div className="mt-0.5 flex items-center justify-between gap-2">
                <span
                  className={`overflow-hidden text-corpo leading-[1.4] text-ellipsis whitespace-nowrap ${
                    caso.nao_lidas ? "text-foreground" : "text-tinta-fraca"
                  }`}
                >
                  {ultima}
                </span>
                <Contador n={caso.nao_lidas} />
              </div>
              {/* A terceira linha é "de que assunto é" à esquerda e "de quem é"
                  à direita. As etiquetas quebram entre si; o titular fica preso
                  na ponta, senão a tese longa o empurra para uma quarta linha e
                  a coluna perde o alinhamento. */}
              {/* UMA linha, e não três: etiqueta é para SINAL (esperando,
                  arquivado, a etiqueta do escritório); etapa e tese são
                  classificação e viram texto discreto. Eram cinco pastilhas
                  quebrando uma por linha — a tese em caixa alta ocupava a coluna
                  inteira, e a etiqueta de mesmo nome repetia a tese logo abaixo. */}
              <div className="mt-1.5 flex items-center gap-2">
                <div className="flex min-w-0 flex-1 items-center gap-[5px] overflow-hidden">
                  {/* ARQUIVADO em primeiro lugar, e não no fim: é o que muda o
                      sentido de tudo o que vem depois na linha. Antes o único
                      sinal era a conversa não estar na lista — e a pastilha
                      "Arquivados" põe vinte linhas idênticas às da mesa viva na
                      tela, onde responder cliente é o gesto de sempre. */}
                  {caso.arquivado ? (
                    <span className="flex-none">
                      <Etiqueta discreta>
                        <span data-dica="Fora da mesa. Mandar mensagem o traz de volta.">arquivado</span>
                      </Etiqueta>
                    </span>
                  ) : null}
                  {esperando && (
                    <span className="flex-none">
                      <Etiqueta cores={CORES_DA_ESPERA} ponto>
                        <span data-dica="Última palavra é do cliente — a resposta está com o escritório">
                          espera {espera(caso.minutos_sem_resposta)}
                        </span>
                      </Etiqueta>
                    </span>
                  )}
                  {/* As ETIQUETAS do escritório: é por elas que alguém varre a mesa
                      procurando "os urgentes". A que repete a tese sai — dizer o
                      mesmo duas vezes na mesma linha era o que a fazia quebrar. */}
                  {/* A etiqueta ENCOLHE com reticências, e não é decepada pela
                      borda: eram `flex-none`, e "Reconhecimento de Vínculo" numa
                      linha com espera e etapa passava por baixo do rosto do
                      titular, cortada no meio da palavra. Ela só cede quando a
                      etapa já está no mínimo, e nunca abaixo de 56px — o começo
                      da palavra ainda se lê, e a dica tem o nome inteiro. */}
                  {tags.slice(0, 2).map((t) => (
                    <span key={t} data-dica={t} className="flex min-w-14 shrink">
                      <Etiqueta classe="max-w-full shrink justify-start" cores={tinta(t, coresDasTags.get(chaveDaEtiqueta(t)))}>
                        <span className="truncate">{t}</span>
                      </Etiqueta>
                    </span>
                  ))}
                  {tags.length > 2 && (
                    <span className="flex-none">
                      <Etiqueta discreta peso={500}>
                        +{tags.length - 2}
                      </Etiqueta>
                    </span>
                  )}
                  {/* Etapa e tese DEPOIS dos sinais, e com o que sobra (`basis-0`):
                      é o texto que cede quando a coluna aperta (inteiro na dica),
                      até 48px: abaixo disso sobrava "D." solto na linha. Antes
                      ele vinha no meio, encolhia junto com a etiqueta e as duas
                      saíam cortadas. A tese vem da planilha em caixa alta; aqui
                      ela se lê como frase. */}
                  <span
                    data-dica={`${caso.etapa ?? ""}${caso.tese ? ` · ${comoFrase(caso.tese)}` : ""}`}
                    className="min-w-12 flex-1 basis-0 truncate text-apoio text-tinta-fraca"
                  >
                    {caso.etapa}
                    {caso.tese ? ` · ${comoFrase(caso.tese)}` : ""}
                  </span>
                </div>
                {/* Por qual número do setor o caso fala — só existe com dois ou
                    mais (`numeroDaLinha` vem vazio com um). Sigla mono de quatro
                    dígitos na ponta da linha, e não disputa largura com o nome. Desligado fica
                    terra, porque responder dali não chega. */}
                {numeroDaLinha ? (
                  <span
                    data-dica={numeroDica}
                    className={`flex-none font-mono text-selo tabular-nums ${
                      numeroDesligado ? "font-bold text-terra" : "font-medium text-tinta-fraca"
                    }`}
                  >
                    {numeroDaLinha}
                  </span>
                ) : null}
              </div>
            </div>
          </button>
        }
      />

      {/* O que o menu do botão direito oferece é o que a mesa já sabe fazer com
          UMA linha, sem abri-la: abrir (aqui ou noutra aba), copiar o link que se
          manda por mensagem, e as duas saídas da mesa. Transferir e delegar NÃO
          entram: as duas pedem escolher pessoa ou modo, e isso é caixa, não item
          de menu. */}
      <ContextMenuContent className="min-w-[210px]">
        {/* A porta da seleção no DEDO: no telefone não há botão direito, e o
            toque longo na linha é o gesto que abre este menu. No computador ela
            também está no cabeçalho da coluna. */}
        <ContextMenuItem onClick={() => aoMarcar(caso.id)}>
          <Icone de={ICONES.certo} tamanho={16} />
          Selecionar
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem onClick={() => aoAbrir(caso.id)}>
          <Icone de={ICONES.chats} tamanho={16} />
          Abrir o atendimento
        </ContextMenuItem>
        {/* "Volto nisso" — o gesto que o WhatsApp tem e a mesa não tinha: a
            conversa lida e resolvida-pela-metade voltava a ser igual às outras,
            e o que a segurava era a memória de quem leu. Fica FORA da conversa
            aberta de propósito: a tela marca lida ao abrir, então marcar sem
            sair se desfaria na releitura seguinte. */}
        <ContextMenuItem onClick={() => aoNaoLer(caso.id)}>
          <Icone de={ICONES.sino} tamanho={16} />
          Marcar como não lida
        </ContextMenuItem>
        <ContextMenuItem onClick={() => window.open(`/atendimentos/${caso.id}`, "_blank", "noopener")}>
          <Icone de={ICONES.avancar} tamanho={16} />
          Abrir em outra aba
        </ContextMenuItem>
        <ContextMenuItem
          onClick={() => {
            /* `?.` porque `clipboard` não existe em contexto sem HTTPS: no IP da
               rede local o item continua na lista e não derruba a tela. */
            void navigator.clipboard?.writeText(`${location.origin}/atendimentos/${caso.id}`);
          }}
        >
          <Icone de={ICONES.encaminhar} tamanho={16} />
          Copiar o link
        </ContextMenuItem>
        <ContextMenuSeparator />
        {/* Etiquetar SEM abrir o caso: é o gesto de quem varre a mesa de manhã
            marcando o que é urgente. Sem isto era preciso abrir a conversa,
            achar o widget na coluna e voltar — três telas para pôr uma palavra
            numa linha que já está sob o cursor. Leva o id DESTA linha, que
            quase nunca é a conversa aberta. */}
        <ContextMenuItem onClick={() => aoEtiquetar(caso.id)}>
          <Icone de={ICONES.etiqueta} tamanho={16} />
          Etiquetas
        </ContextMenuItem>
        <ContextMenuSeparator />
        {caso.arquivado ? (
          <ContextMenuItem onClick={() => aoDesarquivar(caso.id)}>
            <Icone de={ICONES.voltar} tamanho={16} />
            Trazer de volta para a mesa
          </ContextMenuItem>
        ) : (
          <ContextMenuItem onClick={() => aoArquivar(caso.id)}>
            <Icone de={ICONES.arquivar} tamanho={16} />
            Arquivar
          </ContextMenuItem>
        )}
        <ContextMenuItem variant="destructive" onClick={() => aoApagar(caso.id)}>
          <Icone de={ICONES.apagar} tamanho={16} />
          Apagar de vez
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
});

/**
 * A lista carregando, com a FORMA da linha: retrato de 48, nome e hora, prévia,
 * a terceira linha — 94px, a `alturaEstimada` do `ListaLonga`.
 *
 * Era o `Esqueleto` genérico, um retângulo cheio por linha: a chegada trocava
 * sete blocos por sete conversas de desenho diferente, e o olho lia a troca como
 * a tela pulando. Com a mesma geometria a lista só "acende". Uma onda só
 * (`.esqueleto`), sem atraso por linha — sete ondas defasadas parecem sete
 * coisas acontecendo.
 */
function EsqueletoDaLista() {
  return (
    <div role="status" className="-mx-3">
      <span className="sr-only">Carregando os atendimentos…</span>
      {Array.from({ length: 7 }, (_, i) => (
        <div key={i} aria-hidden="true" className="flex h-[94px] gap-3 border-b border-b-foreground/8 px-4 py-3">
          <span className="esqueleto size-12 flex-none rounded-full" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 pt-[3px]">
              <span className="esqueleto h-3.5 w-[55%] rounded-etiqueta" />
              <span className="esqueleto ml-auto h-2.5 w-[34px] rounded-etiqueta" />
            </div>
            <span className="esqueleto mt-2 block h-3 w-[80%] rounded-etiqueta" />
            <span className="esqueleto mt-[9px] block h-2.5 w-[40%] rounded-etiqueta" />
          </div>
        </div>
      ))}
    </div>
  );
}

/*
 * Nenhum atraso aqui — e isso é decisão, não esquecimento.
 *
 * `usePaginacao` (paginacao.js) já espera 140ms antes de pedir a página, e o
 * relógio dele reinicia a cada tecla: o pedido por letra que este arquivo tentou
 * evitar nunca existiu. O atraso que estava aqui apenas SOMAVA — 260ms de espera
 * antes dos 140ms de lá, quase meio segundo entre parar de digitar e a lista
 * mudar, num servidor que responde a busca em 15ms com 528 casos.
 */
function Busca({ carregando }) {
  const { termo, ajustar } = useNavegacao();
  const [texto, definirTexto] = useState(termo);
  const campo = useRef(null);
  /* O último termo que a URL e o campo concordam. Sem ele não dá para separar
     "a pessoa digitou" de "a URL mudou por fora". */
  const combinado = useRef(termo);

  useEffect(() => {
    /* Termo trocado de fora (link colado, botão voltar, outra tela) manda no
       campo. O que a pessoa está digitando, não — é o "nunca sobrescreva o
       rascunho" da mesa, valendo para a busca. */
    if (termo !== combinado.current) {
      combinado.current = termo;
      definirTexto(termo);
    }
  }, [termo]);

  /* "/" traz o foco para a busca, como em toda caixa de conversa que se usa o
     dia inteiro. Só fora de campo de escrita: quem está redigindo a resposta
     digita barra em "R$ 1.000,00 s/ reflexos" — e no composer o "/" é o gesto
     das respostas prontas. */
  useEffect(() => {
    const tecla = (e) => {
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey || escrevendo()) return;
      e.preventDefault();
      campo.current?.focus();
    };
    window.addEventListener("keydown", tecla);
    return () => window.removeEventListener("keydown", tecla);
  }, []);

  return (
    <CampoDeBusca
      rotulo="Buscar atendimento"
      /* Curto o bastante para caber inteiro ao lado do "/" na coluna de 320
         (tablet): "Buscar cliente, telefone, etapa…" saía "Buscar cliente,
         telefc". A lupa já diz "buscar". A etapa continua sendo achada pela busca; quem procura etapa
         usa a barra do funil logo abaixo. */
      dica="Nome ou telefone"
      valor={texto}
      aoMudar={(v) => {
        definirTexto(v);
        /* O texto vai para a URL na hora: o campo não espera a viagem para
           repintar (é o estado local que ele mostra) e a lista não espera duas
           filas. */
        combinado.current = v;
        ajustar({ q: v || null });
      }}
      campo={campo}
      atalho="/"
      /* A lupa vira ampulheta enquanto o servidor responde: sem sinal nenhum,
         a lista antiga em cena com termo novo digitado parece lista errada. */
      ocupado={Boolean(texto) && carregando}
    />
  );
}

/*
 * O filtro da mesa é UM: rostos + funil (`FiltroDaMesa`). As sete pastilhas e
 * a grade escondida saíram em 26/08 — o servidor ainda entende `?filtro=`
 * (a pastilha "esperando resposta" usa), mas a tela não desenha mais a fileira.
 */

/** O rodapé: quem está de pé, quanto tem na fila e o botão de abrir caso. */
/**
 * O que se FAZ nesta coluna, no topo dela.
 *
 * Era uma faixa no pé, embaixo de duzentas conversas: as ações da mesa ficavam
 * onde a rolagem nunca passa e onde o olho só chega depois de desistir de achar.
 * Ação de lista é cabeçalho; o pé da lista é para dizer quantos existem, e isso
 * o `RodapeDaPagina` já faz.
 *
 * Sobrou UM botão nesta fileira, e é de propósito.
 *
 * A fileira dividia a largura da coluna com o ponto verde da equipe, a contagem
 * do recorte e o "Puxar · N" da fila — três coisas de LEITURA espremendo a
 * busca, que é a única que se usa o dia inteiro. O tamanho da lista já está
 * escrito nela logo abaixo, quem está na equipe está nos rostos do filtro, e
 * puxar da fila continua onde ele é decisão e não distração: a tela de abertura
 * ("Puxar próximo da fila") e o círculo tracejado da fila, no filtro.
 */
function AcoesDaLista({ selecionando, aoSelecionar }) {
  const { novoCaso } = useAcoes();
  return (
    <div className="ml-auto flex flex-none items-center gap-2">
      {/* A porta da seleção no COMPUTADOR (no dedo ela é o toque longo na
          linha). Fica aqui, ao lado do "+", porque ação de lista é cabeçalho —
          e é um botão, não um interruptor escondido num menu: "arquivar os
          vinte que este filtro achou" é o gesto que a mesa pedia. */}
      <Botao
        icone={ICONES.certo}
        aoTocar={aoSelecionar}
        destaque={selecionando}
        titulo={selecionando ? "Sair da seleção" : "Selecionar vários atendimentos"}
      />
      <Botao icone={ICONES.somar} aoTocar={novoCaso} destaque titulo="Abrir um atendimento novo" />
    </div>
  );
}

/**
 * A coluna de conversas.
 *
 * A lista é paginada e virtualizada (`ListaLonga`): a busca e a pastilha vão para
 * o SERVIDOR — é ele que tem os 528 casos — e a rolagem pede a página seguinte. A
 * versão anterior filtrava a página carregada de novo na tela e mostrava
 * "200 / 200" achando que 200 era tudo.
 */
export default function Lista() {
  const { mesa, totalDaMesa, mesaCarregando, mesaErro, mesaTemMais, equipe, catalogoDeTags, numerosDoSetor } = useEstado();
  const { maisDaMesa, tentarMaisDaMesa, arquivarCaso, desarquivarCaso, apagarCaso, naoLerCaso } = useAcoes();
  const { vista, casoId, irParaCaso, termo, filtro, etapaDaMesa, respDaMesa, tagDaMesa, numeroDaMesa, ajustar, ir } =
    useNavegacao();
  const irParaComecar = () => ir("/comecar");
  /* Estável, porque entra como prop em duzentas linhas memoizadas: função nova
     a cada render refaz a memoização de todas elas. */
  const etiquetar = useCallback((id) => ajustar({ etiquetas: id }), [ajustar]);
  const celular = useCelular();
  const tablet = useTablet();

  /*
   * O que está marcado — `null` é "modo seleção desligado".
   *
   * Estado LOCAL, e não na URL: seleção não é lugar nem recorte (é o que o
   * `?filtro=` já é), e um link com trinta ids dentro não se manda por mensagem.
   * Também não vai para o `estado` global: nada fora desta coluna precisa saber
   * quem está marcado — a barra de ações nasce aqui.
   *
   * A marca ATRAVESSA a troca de filtro de propósito: marcar os cinco urgentes,
   * trocar para a etiqueta seguinte e marcar mais três é como se monta um lote
   * que nenhum filtro sozinho descreve.
   */
  const [marcados, setMarcados] = useState(null);
  const selecionando = marcados !== null;
  const marcadosSet = useMemo(() => new Set(marcados ?? []), [marcados]);
  /* Marcar uma linha LIGA o modo, se ele estiver desligado: é o toque longo no
     telefone, e o item "Selecionar" do menu do botão direito. */
  const alternarMarca = useCallback(
    (id) => setMarcados((a) => (a === null ? [id] : a.includes(id) ? a.filter((x) => x !== id) : [...a, id])),
    [],
  );

  /* Esc sai da seleção — quem entrou sem querer sai sem aprender nada. Fora de
     campo de escrita, como os outros atalhos desta coluna. */
  useEffect(() => {
    if (!selecionando) return;
    const tecla = (e) => {
      if (e.key !== "Escape" || escrevendo()) return;
      setMarcados(null);
    };
    window.addEventListener("keydown", tecla);
    return () => window.removeEventListener("keydown", tecla);
  }, [selecionando]);

  /* Onde a conversa aberta está na lista: serve para levá-la à vista e para a
     seta saber de onde sai. -1 quando ela não está na página carregada (busca
     em cima, ou caso lá no fim de 528). */
  const indiceAtivo = useMemo(() => (casoId ? mesa.findIndex((c) => c.id === casoId) : -1), [mesa, casoId]);

  /*
   * Alt+↑ / Alt+↓ anda entre conversas sem tirar a mão do teclado.
   *
   * Com Alt porque a seta pelada pertence à rolagem e ao campo de texto — quem
   * está escrevendo uma resposta não pode trocar de cliente ao subir uma linha
   * no que digitou. Anda dentro do que está CARREGADO: o fim da página puxa a
   * próxima pela rolagem, e forçar carregamento pela tecla faria a seta
   * pendurar. Sem conversa aberta, a primeira seta abre a primeira da lista.
   */
  useEffect(() => {
    if (!mesa.length) return;
    const tecla = (e) => {
      if (!e.altKey || (e.key !== "ArrowDown" && e.key !== "ArrowUp")) return;
      /* Não enquanto se escreve: no macOS Alt+seta anda de parágrafo dentro do
         texto, e trocar de cliente no meio de uma resposta é o pior jeito de
         perder o que se estava dizendo. */
      if (escrevendo()) return;
      const daqui = mesa.findIndex((c) => c.id === casoId);
      const alvo = mesa[Math.min(Math.max(daqui + (e.key === "ArrowDown" ? 1 : -1), 0), mesa.length - 1)];
      if (!alvo || alvo.id === casoId) return;
      e.preventDefault();
      irParaCaso(alvo.id);
    };
    window.addEventListener("keydown", tecla);
    return () => window.removeEventListener("keydown", tecla);
  }, [mesa, casoId, irParaCaso]);

  /* Quem é quem na equipe, por nome em caixa alta — o nome no fluxo ("FELIPE")
     não bate com o do cadastro ("Felipe Galera") em caixa. Um mapa estável
     porque a linha é memoizada: recalcular a cada render entraria como prop
     nova em duzentas linhas. */
  const equipePorNome = useMemo(() => new Map((equipe ?? []).map((p) => [p.nome.toUpperCase(), p])), [equipe]);

  /* As cores escolhidas das etiquetas, por chave. Estável porque entra como prop
     em duzentas linhas memoizadas — mapa novo a cada render refaria todas. */
  const coresDasTags = useMemo(() => coresDoCatalogo(catalogoDeTags), [catalogoDeTags]);

  /* Os números do setor por id, com o que a linha imprime já pronto — TEXTO,
     e não o objeto, porque entra como prop em duzentas linhas memoizadas. Com
     um número só o mapa nasce vazio e a linha não mostra nada. */
  const numerosDaLinha = useMemo(() => {
    if ((numerosDoSetor?.length ?? 0) < 2) return null;
    const porId = new Map();
    for (const n of numerosDoSetor) {
      const pareado = conectado(n);
      porId.set(n.id, {
        curto: numeroCurto(n),
        desligado: !pareado,
        dica: `Fala pelo número ${n.rotulo || numeroLegivel(n.numero)}${pareado ? "" : " — desligado: a resposta não sai por ele"}`,
      });
    }
    return { porId, padrao: numerosDoSetor.find((n) => n.padrao)?.id };
  }, [numerosDoSetor]);

  const desenhar = useCallback(
    (caso) => {
      const numero = numerosDaLinha?.porId.get(caso.conexao_id ?? numerosDaLinha.padrao);
      return (
        <LinhaDoCaso
          caso={caso}
          ativo={caso.id === casoId && vista === "chats" && !celular}
          /* Cor e foto vão como texto, e não a pessoa inteira: prop de objeto
           refaz a memoização de cada linha a cada chegada da equipe. */
          corDoTitular={caso.responsavel ? equipePorNome.get(caso.responsavel.toUpperCase())?.cor : undefined}
          fotoDoTitular={caso.responsavel ? equipePorNome.get(caso.responsavel.toUpperCase())?.avatar : undefined}
          aoAbrir={irParaCaso}
          /* As duas saídas da mesa recebem o id da LINHA: sem ele, arquivar pelo
           menu de contexto arquivaria a conversa aberta, não a que foi clicada. */
          aoArquivar={arquivarCaso}
          aoDesarquivar={desarquivarCaso}
          aoApagar={apagarCaso}
          aoEtiquetar={etiquetar}
          aoNaoLer={naoLerCaso}
          coresDasTags={coresDasTags}
          selecionando={selecionando}
          marcado={marcadosSet.has(caso.id)}
          aoMarcar={alternarMarca}
          numeroDaLinha={numero?.curto}
          numeroDesligado={numero?.desligado}
          numeroDica={numero?.dica}
        />
      );
    },
    [
      numerosDaLinha,
      casoId,
      vista,
      celular,
      equipePorNome,
      irParaCaso,
      arquivarCaso,
      desarquivarCaso,
      apagarCaso,
      etiquetar,
      naoLerCaso,
      coresDasTags,
      selecionando,
      marcadosSet,
      alternarMarca,
    ],
  );

  return (
    <div
      /* `data-secao` é o endereço da coluna, como nos widgets — e aqui ele
         responde uma pergunta que nada mais respondia: "a lista está montada?".
         É a pergunta que o cabeçalho da conversa passou a fazer para decidir o
         ← de voltar, e a que o teste de tela mede nas quatro larguras. */
      data-secao="lista"
      className={`flex min-w-0 flex-col overflow-hidden border-r border-r-border bg-secondary ${
        /* 380px (320 no tablet), e não 340/300: a fileira de rostos do filtro
           mora aqui, e em 340 só cabiam quatro pessoas antes de a linha rolar de
           lado — escritório de sete gente ficava com metade da equipe escondida
           atrás de um arrasto. Os 40px vêm da conversa, que tem de sobra: a
           coluna de leitura dela é limitada por `max-w` muito antes disso. */
        celular ? "w-full flex-1" : `flex-none ${tablet ? "w-[320px]" : "w-[380px]"}`
      }`}
    >
      {/* Título, busca e pastilhas são UM bloco, fechado por uma divisória.
          Antes eram três faixas soltas sobre o mesmo bege, e a primeira conversa
          começava sem nada dizer onde o comando acaba e a lista começa — a
          divisória é o que faz o topo virar cabeçalho em vez de mais uma linha. */}
      {/* Título, busca e pastilhas são UM bloco, fechado por uma divisória.
          Antes eram três faixas soltas sobre o mesmo bege, e a primeira conversa
          começava sem nada dizer onde o comando acaba e a lista começa.

          E o bloco emagreceu: título de 27px numa fileira, "N na equipe" noutra
          e as ações no pé da coluna somavam 200px de cabeçalho — duas conversas
          — para mostrar uma palavra, um número e dois botões. Tudo o que é
          COMANDO cabe numa fileira só, e o que era texto virou ponto e dica. */}
      {/* Recuo de 16, o MESMO da linha: eram 18 aqui, 16 na linha e 12 no
          vazio, e a borda esquerda da busca não caía sobre a do retrato. */}
      {/* TETO no topo, e ele rola por conta própria se passar: a lista abaixo é
          `flex-1 min-h-0`, então tudo o que o topo cresce sai DELA. Com o mapa
          de etapas aberto dentro do topo (antes de virar `Popover`) a lista
          chegou a 12px num notebook de 768 — conversa embaixo e rolagem
          nenhuma. Recortes empilhados (tablet, celular) não repetem isso: o
          topo para em 45% da coluna. */}
      <div className="max-h-[45%] flex-none overflow-y-auto border-b border-b-border px-4 pt-3 pb-2.5 [scrollbar-width:thin]">
        {/*
         * No COMPUTADOR o título sai da tela e a busca sobe para a fileira dele.
         *
         * "Atendimentos" ao lado de uma coluna que só mostra atendimentos não diz
         * nada que a mesa já não diga — e custava 36px de altura em cima de toda
         * a lista, o tempo inteiro, que é meia conversa a menos visível em cada
         * abertura. Some do olho, fica para o leitor de tela (`sr-only`), que é
         * quem realmente precisa de um `h1` para saber onde está.
         *
         * No CELULAR ele fica: ali a coluna É a tela, e sem esse nome a pessoa
         * abre o aplicativo sem nada dizendo em que lugar ela caiu.
         */}
        <div className="flex items-center gap-2">
          <h1
            className={`m-0 min-w-0 overflow-hidden font-serif text-ellipsis whitespace-nowrap ${
              celular ? "text-titulo" : "sr-only"
            }`}
          >
            {TITULOS[vista]}
          </h1>
          {/* A busca ocupa o lugar que era do título — mesma fileira, mesma
              altura, e a coluna inteira ganha a faixa que sobrou. */}
          {!celular && (
            <div className="min-w-0 flex-1">
              <Busca carregando={mesaCarregando} />
            </div>
          )}
          <AcoesDaLista selecionando={selecionando} aoSelecionar={() => setMarcados(selecionando ? null : [])} />
        </div>

        {celular && (
          <div className="mt-[9px]">
            <Busca carregando={mesaCarregando} />
          </div>
        )}

        {/* O filtro fica montado mesmo com busca digitada: recorte que age é
            recorte que se vê. Os números do funil vêm da matriz contada sobre a
            busca atual; a pastilha "esperando" ignora o termo de propósito. */}
        {vista === "chats" && <FiltroDaMesa />}

        {/* A faixa dos três primeiros passos SAIU em 09/09, por pedido do
            escritório. Ela ensinava a abrir, responder e mover — e o preço era
            uma faixa no alto da coluna de trabalho, todos os dias, para quem já
            sabe. O rastro era do NAVEGADOR (`localStorage`), então máquina nova
            e aba limpa faziam ela voltar para quem usa a mesa desde agosto.
            O que ela dizia continua dito onde o passo acontece: o bloco AGORA da
            ficha diz a próxima ação do caso, o trilho mostra onde ele está, e o
            "?" de cada tela leva ao capítulo do manual. */}
      </div>

      {/* A barra do lote nasce ABAIXO do filtro, e é de propósito: o gesto que a
          mesa pedia é "faça isso nos que este filtro está mostrando". Esconder o
          filtro para mostrar a barra tiraria da tela o que define o lote. */}
      {selecionando && vista === "chats" && (
        <SelecaoDeCasos marcados={marcados} definir={setMarcados} sair={() => setMarcados(null)} />
      )}

      {mesa.length ? (
        <ListaLonga
          itens={mesa}
          desenhar={desenhar}
          rotulo="Atendimentos"
          alturaEstimada={94}
          espaco={0}
          /* No celular a lista e a conversa não dividem a tela: rolar a lista
             para o caso aberto seria mexer numa coluna que ninguém está vendo. */
          indiceAlvo={celular ? null : indiceAtivo}
          estilo="padding:0"
          aoPedirMais={maisDaMesa}
          temMais={mesaTemMais}
          carregando={mesaCarregando}
          rodape={
            <RodapeDaPagina
              carregando={mesaCarregando}
              erro={mesaErro}
              temMais={mesaTemMais}
              quantos={mesa.length}
              total={totalDaMesa}
              aoTentar={tentarMaisDaMesa}
              nome="atendimentos"
            />
          }
        />
      ) : (
        <div className="flex-1 overflow-y-auto px-3 pb-4">
          {/* O ERRO vem antes do vazio: falhando a PRIMEIRA página (servidor
              caído, rede oscilando, papel sem `leads:ver`) o ramo do `ListaLonga`
              não existe, o rodapé com o motivo e o "Tentar de novo" iam junto e a
              coluna dizia "Nada por aqui" sem filtro nem busca ativos. A pessoa
              lia "mesa vazia" e acreditava. É a mesma peça do rodapé. */}
          {mesaErro ? (
            <RodapeDaPagina erro={mesaErro} aoTentar={tentarMaisDaMesa} nome="atendimentos" />
          ) : mesaCarregando ? (
            /* 94px é a altura estimada da linha do caso em `ListaLonga`: o
               esqueleto e a lista têm de medir o mesmo, senão a chegada empurra. */
            <EsqueletoDaLista />
          ) : termo || etapaDaMesa || respDaMesa || tagDaMesa || numeroDaMesa || (filtro && filtro !== "Todos") ? (
            <Vazio
              titulo="Nada com este recorte"
              acao={{
                rotulo: "Limpar o recorte",
                aoTocar: () => ajustar({ q: null, etapa: null, resp: null, filtro: null, tag: null, numero: null }),
              }}
            >
              {termo
                ? `Nenhum atendimento para "${termo}"`
                : etapaDaMesa || respDaMesa
                  ? "Nenhum atendimento nesta célula da matriz"
                  : "Nenhum atendimento nesta pastilha"}{" "}
              — limpe a busca ou toque no rosto/etapa marcado para desfazer.
            </Vazio>
          ) : (
            /* Sem busca e sem pastilha, vazio é vazio de verdade: a porta é o
               "+ Novo" do rodapé desta mesma coluna. */
            <Vazio
              titulo="Nenhum atendimento ainda"
              /* No celular a lista É a tela inicial — a boas-vindas da mesa (com
                 os primeiros passos) não aparece ali. A porta vem para cá. */
              segunda={celular ? { rotulo: "Primeiros passos", aoTocar: () => irParaComecar() } : undefined}
            >
              O primeiro caso entra por "+ Novo", aqui embaixo, ou pelo WhatsApp quando a conexão estiver de pé.
            </Vazio>
          )}
        </div>
      )}
    </div>
  );
}
